import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CalendarClock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export function AuthShell({ children, title, subtitle }) {
  return (
    <div className="flex min-h-screen bg-white">
      <div className="hidden w-1/2 lg:block relative">
        <img
          src="https://images.pexels.com/photos/6809658/pexels-photo-6809658.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="Accueil professionnel"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-slate-900/60" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <p className="font-display text-2xl font-bold">La prise de rendez-vous pensée pour le mobile.</p>
          <p className="mt-2 text-sm text-slate-300">Un lien envoyé par SMS, vos clients réservent en moins d'une minute.</p>
        </div>
      </div>
      <div className="flex w-full items-center justify-center px-6 lg:w-1/2">
        <div className="w-full max-w-sm">
          <div className="mb-8 flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-600">
              <CalendarClock className="h-5 w-5 text-white" />
            </div>
            <span className="font-display text-xl font-bold text-slate-900">RendezVous</span>
          </div>
          <h1 className="font-display text-2xl font-bold text-slate-900">{title}</h1>
          <p className="mt-1 mb-6 text-sm text-slate-500">{subtitle}</p>
          {children}
        </div>
      </div>
    </div>
  );
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(email, password);
      navigate(user.is_platform_admin ? "/admin" : "/");
    } catch (err) {
      setError(formatApiError(err, "Connexion impossible"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Connexion" subtitle="Accédez à votre espace entreprise">
      <form onSubmit={submit} className="space-y-4">
        {error && <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="login-error">{error}</p>}
        <div>
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" className="mt-1" />
        </div>
        <div>
          <Label htmlFor="password">Mot de passe</Label>
          <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" className="mt-1" />
        </div>
        <div className="text-right">
          <Link to="/forgot-password" className="text-sm text-blue-600 hover:underline" data-testid="link-forgot-password">
            Mot de passe oublié ?
          </Link>
        </div>
        <Button type="submit" className="w-full" disabled={loading} data-testid="login-form-submit-button">
          {loading ? "Connexion..." : "Se connecter"}
        </Button>
        <p className="text-center text-sm text-slate-500">
          Pas encore de compte ?{" "}
          <Link to="/register" className="font-medium text-blue-600 hover:underline" data-testid="link-register">
            Créer un compte
          </Link>
        </p>
      </form>
    </AuthShell>
  );
}
